import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, MapPin, ChevronRight } from "lucide-react";

const PATHS = [
  { id: "beginner", title: "Beginner", color: "bg-saffron", subtitle: "Open the door." },
  { id: "builder", title: "Builder", color: "bg-teal text-cream", subtitle: "Build real apps." },
  { id: "professional", title: "Professional", color: "bg-ink text-cream", subtitle: "Ship to the App Store." },
  { id: "games", title: "Game Dev", color: "bg-marigold text-cream", subtitle: "Build playable Apple games." },
];

export default function Lessons() {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    api.get("/lessons").then(({ data }) => setLessons(data));
    if (user) api.get("/lessons/progress/me").then(({ data }) => {
      const map = {};
      data.forEach(p => { map[p.lesson_id] = p.completed; });
      setProgress(map);
    });
  }, [user]);

  return (
    <div className="container-narrow py-12 md:py-16" data-testid="lessons-page">
      <div className="text-center mb-12">
        <div className="label-eyebrow mb-3">The adventure map</div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-tight">
          Three trails. <span className="italic text-marigold">One steady guide.</span>
        </h1>
        <p className="text-ink2 mt-4 max-w-2xl mx-auto">
          A winding path from "what is an app?" to your first build on the App Store.
        </p>
        <div className="mt-6">
          <Link
            to="/library"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink bg-cream hover:bg-marigold hover:text-cream transition text-sm font-medium"
            data-testid="lessons-library-link"
          >
            Or hit the road trip — every stop opens Ms. Pradhan's chapters from the curriculum →
          </Link>
        </div>
      </div>

      {PATHS.map((p, idx) => {
        const items = lessons.filter(l => l.path === p.id);
        return (
          <section key={p.id} className="mb-20" data-testid={`path-${p.id}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className={`${p.color} rounded-full px-5 py-2 border-2 border-ink shadow-[4px_4px_0_0_#1A1B41] inline-flex items-center gap-2`}>
                <MapPin className="w-4 h-4" /> <span className="font-heading font-semibold">{p.title}</span>
              </div>
              <div className="text-ink2 italic">{p.subtitle}</div>
            </div>

            <div className="relative">
              {/* winding path SVG */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none hidden md:block" preserveAspectRatio="none" viewBox="0 0 1000 400">
                <path
                  d={`M 50 60 C 250 ${idx % 2 === 0 ? 180 : 20}, 500 ${idx % 2 === 0 ? 20 : 200}, 750 200 S 950 360, 950 360`}
                  fill="none" stroke="#1A1B41" strokeWidth="2" strokeDasharray="6 8" opacity="0.25"
                />
              </svg>

              <div className="grid md:grid-cols-3 gap-5 relative">
                {items.map((l, i) => (
                  <Link
                    key={l.id}
                    to={`/lessons/${l.id}`}
                    className="card-soft p-5 group hover:-translate-y-1 transition-all duration-300 relative"
                    data-testid={`lesson-card-${l.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 border-ink ${progress[l.id] ? "bg-teal text-cream" : "bg-cream text-ink"}`}>
                        {progress[l.id] ? <Check className="w-4 h-4" /> : i + 1}
                      </div>
                      <span className="text-xs text-ink2">{l.duration}</span>
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-1.5 leading-snug">{l.title}</h3>
                    <p className="text-sm text-ink2 leading-relaxed mb-3">{l.summary}</p>
                    <span className="inline-flex items-center text-sm text-marigold font-medium group-hover:gap-1.5 gap-1 transition-all">
                      Open lesson <ChevronRight className="w-4 h-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
